import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, CalendarDays, Check, ChevronLeft, ChevronRight, Download, Link2, Linkedin, Users, X, Zap,
} from 'lucide-react';
import { TeamsLogo } from '../BrandIcons';
import { gamificationService } from '../../services/api';
import { MedalArt } from './MedalArt';
import './MedalModal.css';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }).replace('.', '') : '');

/**
 * Modal da medalha (A6): abre perto do clique, setas percorrem as medalhas, Esc/X/fora fecham.
 * Conquistada: raios, brilho e faíscas + compartilhar (página pública só com a medalha, o nome e o curso).
 * Não conquistada: progresso e a ação que leva até ela.
 */
export function MedalModal({ medals, index, onIndex, onClose, anchorY, courseSlug, actionFor }) {
  const m = medals[index];
  const cardRef = useRef(null);
  const [top, setTop] = useState(null);
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onIndex((index + 1) % medals.length);
      if (e.key === 'ArrowLeft') onIndex((index - 1 + medals.length) % medals.length);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [index, medals.length, onClose, onIndex]);

  // Perto do clique, sem passar do fim da tela
  useLayoutEffect(() => {
    const h = cardRef.current?.offsetHeight || 560;
    const want = (anchorY ?? window.innerHeight / 2) - h / 2;
    setTop(Math.max(16, Math.min(want, window.innerHeight - h - 16)));
  }, [anchorY, index]);

  useEffect(() => {
    setShare(null);
    setCopied(false);
    setError(null);
  }, [index]);

  const link = async () => {
    if (share) return share;
    try {
      const r = await gamificationService.shareMedal(courseSlug, m.key);
      const s = { url: `${window.location.origin}${r.path}`, image: `/api/public/medals/${r.token}/og.png` };
      setShare(s);
      return s;
    } catch {
      setError('Não deu para criar o link agora.');
      return null;
    }
  };

  const open = (href) => window.open(href, '_blank', 'noopener,noreferrer,width=680,height=720');
  const onLinkedIn = async () => {
    const s = await link();
    if (!s) return;
    const text = `Conquistei a medalha "${m.name}" no StudySpace: ${m.description.charAt(0).toLowerCase()}${m.description.slice(1)}.\n\n${s.url}`;
    open(`https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`);
  };
  const onTeams = async () => {
    const s = await link();
    if (s) open(`https://teams.microsoft.com/share?href=${encodeURIComponent(s.url)}&msgText=${encodeURIComponent(`Conquistei a medalha "${m.name}" no StudySpace`)}`);
  };
  const onCopy = async () => {
    const s = await link();
    if (!s) return;
    try {
      await navigator.clipboard.writeText(s.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Seu navegador não deixou copiar.');
    }
  };
  const onDownload = async () => {
    const s = await link();
    if (!s) return;
    const a = document.createElement('a');
    a.href = s.image;
    a.download = `medalha-${m.key}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const p = m.progress || { pct: 0, label: '' };
  const action = !m.unlocked ? actionFor?.(m) : null;

  return createPortal(
    <div className="mm-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`mm ${m.unlocked ? 'is-won' : 'is-locked'}`} ref={cardRef} role="dialog" aria-modal="true"
           aria-label={m.name} style={{ top: top ?? '50%', transform: top == null ? 'translateY(-50%)' : undefined }}>
        <div className="mm__hero">
          <button type="button" className="mm__close" onClick={onClose} aria-label="Fechar"><X size={16} aria-hidden="true" /></button>
          {medals.length > 1 && (
            <>
              <button type="button" className="mm__nav mm__nav--prev" onClick={() => onIndex((index - 1 + medals.length) % medals.length)} aria-label="Medalha anterior">
                <ChevronLeft size={18} aria-hidden="true" />
              </button>
              <button type="button" className="mm__nav mm__nav--next" onClick={() => onIndex((index + 1) % medals.length)} aria-label="Próxima medalha">
                <ChevronRight size={18} aria-hidden="true" />
              </button>
            </>
          )}
          <MedalArt key={m.key} icon={m.icon} unlocked={m.unlocked} pct={p.pct} />
          <span className="mm__eyebrow">{m.unlocked ? 'Medalha conquistada' : 'Ainda não conquistada'}</span>
          <h2 className="mm__name">{m.name}</h2>
          <p className="mm__desc">{m.description}</p>
          <div className="mm__chips">
            {m.unlocked ? (
              <>
                {m.unlockedAt && <span><CalendarDays size={13} aria-hidden="true" />Conquistada em {fmtDate(m.unlockedAt)}</span>}
                <span><Zap size={13} aria-hidden="true" />+{m.xp} XP</span>
                <span><Users size={13} aria-hidden="true" />{m.holdersPct}% das pessoas têm</span>
              </>
            ) : (
              <>
                <span><Zap size={13} aria-hidden="true" />vale +{m.xp} XP</span>
              </>
            )}
          </div>
        </div>
        {m.unlocked ? (
          <div className="mm__foot">
            <span className="mm__label">Compartilhar</span>
            <div className="mm__share">
              <button type="button" onClick={onLinkedIn}><span className="mm__ic mm__ic--li"><Linkedin size={16} aria-hidden="true" /></span>LinkedIn</button>
              <button type="button" onClick={onTeams}><span className="mm__ic mm__ic--teams"><TeamsLogo size={17} /></span>Teams</button>
              <button type="button" onClick={onCopy} className={copied ? 'is-done' : ''}>
                <span className="mm__ic">{copied ? <Check size={16} aria-hidden="true" /> : <Link2 size={16} aria-hidden="true" />}</span>{copied ? 'Copiado' : 'Copiar link'}
              </button>
              <button type="button" onClick={onDownload}><span className="mm__ic"><Download size={16} aria-hidden="true" /></span>Baixar imagem</button>
            </div>
            <p className="mm__note">{error || 'O link abre uma página pública só com a medalha, seu nome e o curso. Nenhum outro dado vai junto.'}</p>
          </div>
        ) : (
          <div className="mm__foot">
            <div className="mm__progress">
              <span className="mm__label">Seu progresso</span>
              <strong>{p.label || `${p.pct}%`}</strong>
            </div>
            <span className="mm__bar"><span style={{ width: `${p.pct}%` }} /></span>
            {action && (
              <button type="button" className="mm__cta" onClick={() => { onClose(); navigate(action.to); }}>
                {action.label}<ArrowRight size={15} aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
